import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
	Alert,
	AlertColor,
	Backdrop,
	Box,
	CircularProgress,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	TextField,
	Toolbar,
	Typography,
} from '@mui/material';
import { formatTransactionAmount } from 'features/transaction-details/helpers';
import { checkShowErrorMessage } from '../../../common/helpers';
import { extractErrorMessage } from 'features/quotation/helpers';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import { getOperators } from 'features/transactions/services';
import { IOperators } from 'features/transactions/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { postETicket, searchCrossesByEticket } from 'features/authorization/services';
import { analyzeEticketCrossHistory, EticketCrossCheckResult } from 'features/authorization/helpers/eticketCrossCheck';
import { getAuthorizationCrossExpiryWarning } from 'features/authorization/helpers/authorizationCrossExpiryWarning';
import {
	buildLockedOperatorMessage,
	computeRemainingPendingAfterCross,
	getOperatorDisplayName,
	isLowRemainingPendingAfterCross,
	parseCrossAmountInput,
} from 'features/authorization/helpers/crossOperatorLock';
import { EticketCrossCheckPanel } from './EticketCrossCheckPanel';

function PaperComponent(props: PaperProps) {
	return (
		<Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
			<Paper {...props} />
		</Draggable>
	);
}

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
}));

interface ShowAlertState {
	show: boolean;
	severity: AlertColor;
	message: string;
}

const MIN_ETICKET_LENGTH = 8;
const CROSS_CHECK_DEBOUNCE_MS = 450;

interface ETicketFormValues {
	authorizationId: number;
	amount: number | string;
	eticket: string;
	operator: string;
}

export interface CreateETicketModalProps {
	authorizationId: number;
	transactionId?: number;
	operators: { value: string; description: string }[];
	/** Operador obligatorio si ya hay otro e-ticket cruzado en esta autorización. */
	lockedOperatorCode?: string | null;
	/** Saldo sin cruzar de la autorización (disponible para este cruce). */
	pendingToCross?: number;
	coinCode?: string;
	authorizationCreatedAt?: string;
	chargedAgainstOptionCode?: string | null;
	chargedAgainstOptionName?: string | null;
	open: boolean;
	handleClose: () => void;
	reloadList: () => void;
}

export function CreateETicketModal({
	authorizationId,
	operators: operatorsProp,
	lockedOperatorCode = null,
	pendingToCross = 0,
	coinCode = 'USD',
	authorizationCreatedAt = '',
	chargedAgainstOptionCode = null,
	chargedAgainstOptionName = null,
	open,
	handleClose,
	reloadList,
}: CreateETicketModalProps): JSX.Element {
	const postRefundDefaultValues: ETicketFormValues = {
		authorizationId,
		amount: '',
		eticket: '',
		operator: '',
	};

	const classes = useStyles();
	const {
		control,
		handleSubmit,
		formState: { errors, isValid },
		reset,
		setValue,
	} = useForm<ETicketFormValues>({ mode: 'all', defaultValues: postRefundDefaultValues });
	const eticketValue = useWatch({ control, name: 'eticket' });
	const amountValue = useWatch({ control, name: 'amount' });

	const [loading, setLoading] = useState<boolean>(false);
	const [operators, setOperators] = useState<IOperators[]>([]);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [operatorsLoaded, setOperatorsLoaded] = useState(false);

	const [crossCheck, setCrossCheck] = useState<EticketCrossCheckResult | null>(null);
	const [checkingCross, setCheckingCross] = useState(false);
	const [crossCheckError, setCrossCheckError] = useState<string | null>(null);
	const [verifiedEticket, setVerifiedEticket] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	const eticketInputRef = useRef<HTMLInputElement>(null);

	const resetCrossCheck = useCallback((): void => {
		abortRef.current?.abort();
		setCrossCheck(null);
		setCrossCheckError(null);
		setVerifiedEticket(null);
		setCheckingCross(false);
	}, []);

	const runCrossCheck = useCallback(
		async (trimmed: string): Promise<EticketCrossCheckResult | null> => {
			abortRef.current?.abort();
			const controller = new AbortController();
			abortRef.current = controller;
			setCheckingCross(true);
			setCrossCheckError(null);

			try {
				const response = await searchCrossesByEticket(trimmed, controller.signal);
				if (controller.signal.aborted) return null;
				const rows = response.data.data ?? [];
				const result = analyzeEticketCrossHistory(rows, authorizationId);
				setCrossCheck(result);
				setVerifiedEticket(trimmed);
				return result;
			} catch {
				if (controller.signal.aborted) return null;
				setCrossCheck(null);
				setVerifiedEticket(null);
				setCrossCheckError('No se pudo verificar el historial de cruces. Intentá de nuevo.');
				return null;
			} finally {
				if (!controller.signal.aborted) {
					setCheckingCross(false);
				}
			}
		},
		[authorizationId],
	);

	useEffect(() => {
		if (!open) {
			resetCrossCheck();
			return;
		}

		const trimmed = (eticketValue ?? '').trim();
		if (trimmed.length < MIN_ETICKET_LENGTH) {
			resetCrossCheck();
			return;
		}

		const timer = window.setTimeout(() => {
			void runCrossCheck(trimmed);
		}, CROSS_CHECK_DEBOUNCE_MS);

		return () => {
			window.clearTimeout(timer);
		};
	}, [eticketValue, open, runCrossCheck, resetCrossCheck]);

	const trimmedEticket = (eticketValue ?? '').trim();
	const crossCheckReady =
		verifiedEticket === trimmedEticket && trimmedEticket.length >= MIN_ETICKET_LENGTH && !checkingCross;
	const canSubmitCross = crossCheckReady && crossCheck?.canProceed === true;

	const operatorOptions =
		(operatorsProp?.length ?? 0) > 0
			? operatorsProp
			: operators.map((op) => ({ value: op.value, description: op.description }));

	const lockedOperatorName = lockedOperatorCode ? getOperatorDisplayName(lockedOperatorCode, operatorOptions) : null;

	const amountEntered = parseCrossAmountInput(amountValue);
	const remainingPendingAfterCross = computeRemainingPendingAfterCross(pendingToCross, amountEntered);
	const availableToCrossLabel = formatTransactionAmount(pendingToCross, coinCode);

	const crossExpiryWarning = useMemo(
		() =>
			authorizationCreatedAt
				? getAuthorizationCrossExpiryWarning(authorizationCreatedAt, chargedAgainstOptionCode, chargedAgainstOptionName)
				: null,
		[authorizationCreatedAt, chargedAgainstOptionCode, chargedAgainstOptionName],
	);

	const onSubmit = async (data: ETicketFormValues): Promise<void> => {
		const eticket = data.eticket.trim();
		if (eticket.length < MIN_ETICKET_LENGTH) return;

		if (lockedOperatorCode && data.operator !== lockedOperatorCode) {
			setAlert({
				show: true,
				severity: 'warning',
				message: buildLockedOperatorMessage(lockedOperatorName ?? lockedOperatorCode),
			});
			return;
		}

		let check = crossCheck;
		if (!crossCheckReady || verifiedEticket !== eticket) {
			check = await runCrossCheck(eticket);
		}
		if (!check?.canProceed) {
			setAlert({
				show: true,
				severity: 'warning',
				message: check?.message ?? 'Verificá el e-ticket antes de confirmar el cruce.',
			});
			return;
		}

		try {
			setLoading(true);
			await postETicket({
				authorizationId: data.authorizationId,
				amount: String(data.amount),
				eticket,
				operator: data.operator,
			});
			setAlert({
				show: true,
				severity: 'success',
				message: 'E-Ticket cruzado satisfactoriamente',
			});
			reset();
			resetCrossCheck();
			reloadList();
			handleClose();
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al cruzar el E-Ticket'),
			});
		} finally {
			setLoading(false);
		}
	};

	const getOperatorsInfo = async (): Promise<void> => {
		try {
			setLoading(true);
			setOperators((await getOperators()).data);
			setOperatorsLoaded(true);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al recibir el listado de operadores'),
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open && !operatorsLoaded) {
			if (operatorsProp && operatorsProp.length > 0) {
				setOperators(operatorsProp.map((op) => ({ value: op.value, description: op.description })));
				setOperatorsLoaded(true);
			} else {
				void getOperatorsInfo();
			}
		}
		if (!open && operatorsLoaded) {
			setOperatorsLoaded(false);
			reset();
			resetCrossCheck();
		}
	}, [open, operatorsLoaded, operatorsProp, reset, resetCrossCheck]);

	useEffect(() => {
		if (!open) return;
		const timer = window.setTimeout(() => {
			eticketInputRef.current?.focus();
		}, 150);
		return () => window.clearTimeout(timer);
	}, [open]);

	useEffect(() => {
		if (!open) return;
		if (lockedOperatorCode) {
			setValue('operator', lockedOperatorCode, { shouldValidate: true });
		}
	}, [open, lockedOperatorCode, setValue]);

	return (
		<div>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Dialog open={open} PaperComponent={PaperComponent} aria-labelledby="draggable-dialog-title">
				<Toolbar
					id="draggable-dialog-title"
					style={{ cursor: 'move' }}
					sx={{ display: 'flex', justifyContent: 'space-between' }}
				>
					<Typography variant="h5" color="primary">
						Agregar E-Ticket
					</Typography>
					<IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
						<CloseIcon />
					</IconButton>
				</Toolbar>
				<DialogContent>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Grid container gap={2}>
							{crossExpiryWarning && (
								<Grid item xs={12}>
									<Alert severity="warning" variant="outlined">
										<Typography variant="body2">{crossExpiryWarning.message}</Typography>
									</Alert>
								</Grid>
							)}
							<Grid item xs={12}>
								<Controller
									name="eticket"
									control={control}
									rules={{
										required: { value: true, message: 'Este campo es requerido' },
										minLength: {
											value: MIN_ETICKET_LENGTH,
											message: `Ingresá al menos ${MIN_ETICKET_LENGTH} caracteres`,
										},
										maxLength: {
											value: 250,
											message: 'El E-Ticket tiene un máximo de 250 caracteres',
										},
									}}
									render={({ field }) => (
										<TextField
											variant="outlined"
											required
											fullWidth
											label="E-Ticket"
											type="text"
											disabled={loading}
											helperText={
												checkShowErrorMessage(Boolean(errors.eticket), errors.eticket?.message) ||
												'Se verificará si el boleto tiene cruces previos antes de confirmar.'
											}
											error={Boolean(errors.eticket)}
											{...field}
											inputRef={(element) => {
												field.ref(element);
												eticketInputRef.current = element;
											}}
										/>
									)}
								/>
								<EticketCrossCheckPanel
									result={crossCheck}
									checking={checkingCross}
									checkError={crossCheckError}
									eticketValue={trimmedEticket}
								/>
							</Grid>
							<Grid item xs={12}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'flex-start',
										gap: 2,
										flexWrap: { xs: 'wrap', sm: 'nowrap' },
									}}
								>
									<Controller
										name="amount"
										control={control}
										rules={{
											required: { value: true, message: 'Este campo es requerido' },
											min: {
												value: 1,
												message: 'El monto debe ser mayor que 0',
											},
											pattern: {
												value: /^(0|[1-9]\d*)(\.\d+)?$/,
												message: 'El valor debe ser numérico',
											},
											validate: (value) => {
												const amount = parseCrossAmountInput(value);
												if (amount == null || pendingToCross <= 0) return true;
												if (amount > pendingToCross) {
													return `El monto no puede superar lo disponible para cruzar (${availableToCrossLabel})`;
												}
												return true;
											},
										}}
										render={({ field }) => (
											<TextField
												variant="outlined"
												required
												fullWidth
												label="Monto"
												type="number"
												disabled={loading}
												helperText={checkShowErrorMessage(Boolean(errors.amount), errors.amount?.message)}
												error={Boolean(errors.amount)}
												sx={{ flex: 1, minWidth: 160 }}
												{...field}
											/>
										)}
									/>
									{pendingToCross > 0 && (
										<Box sx={{ pt: 2, flexShrink: 0 }}>
											<Typography variant="body2" color="text.secondary">
												Disponible:{' '}
												<Typography component="span" variant="body2" fontWeight={600} color="warning.main">
													{availableToCrossLabel}
												</Typography>
											</Typography>
										</Box>
									)}
								</Box>
								{remainingPendingAfterCross != null && remainingPendingAfterCross > 0 && (
									<Alert
										severity={isLowRemainingPendingAfterCross(remainingPendingAfterCross) ? 'error' : 'info'}
										variant="outlined"
										sx={{ mt: 1.5, py: 0.5 }}
									>
										<Typography variant="body2">
											Haciendo este cruce quedarán pendientes por cruzar en esta autorización{' '}
											<strong>{formatTransactionAmount(remainingPendingAfterCross, coinCode)}</strong>.
										</Typography>
									</Alert>
								)}
							</Grid>
							<Grid item xs={12}>
								{lockedOperatorCode && lockedOperatorName && (
									<Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0.5 }}>
										<Typography variant="body2">{buildLockedOperatorMessage(lockedOperatorName)}</Typography>
									</Alert>
								)}
								<Controller
									name="operator"
									control={control}
									rules={{
										required: {
											value: true,
											message: 'Por favor seleccione el Operator del ETicket',
										},
									}}
									render={({ field }) => (
										<FormControl sx={{ width: '100%' }}>
											<InputLabel id="operator-label">Operador</InputLabel>
											<Select
												labelId="operator-label"
												id="operator-select"
												label="Operador"
												disabled={!operators.length || loading || Boolean(lockedOperatorCode)}
												sx={{ width: '100%' }}
												{...field}
											>
												{operators.map((operator) => (
													<MenuItem
														key={operator.value}
														value={operator.value}
														disabled={Boolean(lockedOperatorCode) && operator.value !== lockedOperatorCode}
													>
														{operator.description}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									)}
								/>
							</Grid>
							<Grid item xs={12} display={'flex'} gap={'10px'} justifyContent="flex-end">
								<Button
									variant="contained"
									disabled={loading || !isValid || !canSubmitCross || checkingCross}
									color="success"
									fullWidth
									type="submit"
								>
									Confirmar cruce
								</Button>
							</Grid>
						</Grid>
					</form>
				</DialogContent>
			</Dialog>
			<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</div>
	);
}
